import { supabase } from './supabase';
import { BaseService } from './base-service';
import { CategoryService } from './category-service';

/**
 * Interface representing a product specification
 */
export interface ProductSpecification {
  name: string;
  value: string;
}

/**
 * Interface representing a product
 */
export interface Product {
  id: string;
  name: string;
  slug: string;
  category_id: string;
  subcategory_id: string;
  product_category_id: string;
  supplier_id: string;
  condition: 'New' | 'Used' | 'Refurbished';
  price: number;
  previous_price?: number;
  currency: string;
  description?: string;
  features?: string[];
  year?: number;
  manufacturer?: string;
  model?: string;
  hours_used?: number;
  location?: string;
  rating?: number;
  review_count?: number;
  listed_date?: string;
  financing_available?: boolean;
  warranty?: string;
  in_stock?: boolean;
  delivery_available?: boolean;
  delivery_fee?: number;
  featured?: boolean;
  views_count?: number;
  created_at?: string;
  updated_at?: string;
}

/**
 * Service for managing products
 */
export class ProductService extends BaseService {
  private categoryService: CategoryService;
  
  constructor() {
    super(supabase);
    this.categoryService = new CategoryService();
  }
  
  /**
   * Get a product by ID
   * @param id The product ID
   * @returns The product or null if not found
   */
  async getProduct(id: string): Promise<Product | null> {
    try {
      const { data, error } = await this.supabase
        .from('products')
        .select('*')
        .eq('id', id)
        .single();
        
      if (error) throw error;
      return data;
    } catch (error) {
      this.handleError(error, 'fetching product');
      return null;
    }
  }
  
  /**
   * Get products by category ID
   * @param categoryId The category ID
   * @returns Array of products or empty array if none found
   */
  async getProductsByCategoryId(categoryId: string): Promise<Product[]> {
    try {
      const { data, error } = await this.supabase
        .from('products')
        .select(`
          *,
          product_images (
            image_url,
            is_primary,
            display_order
          ),
          accounts!supplier_id (
            supplier_profiles (
              business_name,
              slug,
              logo_url,
              rating,
              review_count,
              response_rate,
              mobile_number,
              verified_supplier,
              certified_supplier,
              is_leading_supplier
            ),
            mobile_number,
            phone
          )
        `)
        .eq('category_id', categoryId);
        
      if (error) throw error;
      
      // Format the products to include image URLs and supplier information
      const formattedProducts = (data || []).map(product => {
        // Extract primary image or first image
        const productImages = product.product_images || [];
        const primaryImage = productImages.find((img: any) => img.is_primary);
        const firstImage = productImages.length > 0 ? productImages[0] : null;
        
        // Extract all image URLs sorted by display_order
        const images = productImages
          .sort((a: any, b: any) => (a.display_order || 0) - (b.display_order || 0))
          .map((img: any) => img.image_url);
        
        // Extract supplier information through accounts
        const supplierProfile = product.accounts?.supplier_profiles;
        const accountInfo = product.accounts;
        
        // Remove the nested objects and add formatted data
        const { product_images, accounts, ...productData } = product;
        
        return {
          ...productData,
          images,
          main_image: (primaryImage || firstImage)?.image_url || null,
          // Add supplier information from the database
          supplier_business_name: supplierProfile?.business_name,
          supplier_slug: supplierProfile?.slug,
          supplier_logo_url: supplierProfile?.logo_url,
          supplier_rating: supplierProfile?.rating || 0,
          supplier_review_count: supplierProfile?.review_count || 0,
          supplier_response_rate: supplierProfile?.response_rate || 0,
          supplier_mobile_number: supplierProfile?.mobile_number || accountInfo?.mobile_number || accountInfo?.phone,
          supplier_verified: supplierProfile?.verified_supplier || false,
          supplier_certified: supplierProfile?.certified_supplier || false,
          is_leading_supplier: supplierProfile?.is_leading_supplier || false,
        };
      });
      
      return formattedProducts;
    } catch (error) {
      this.handleError(error, 'fetching products by category');
      return [];
    }
  }
  
  /**
   * Get products by subcategory ID
   * @param subcategoryId The subcategory ID
   * @returns Array of products or empty array if none found
   */
  async getProductsBySubcategoryId(subcategoryId: string): Promise<Product[]> {
    try {
      const { data, error } = await this.supabase
        .from('products')
        .select(`
          *,
          product_images (
            image_url,
            is_primary,
            display_order
          ),
          accounts!supplier_id (
            supplier_profiles (
              business_name,
              slug,
              logo_url,
              rating,
              review_count,
              response_rate,
              mobile_number,
              verified_supplier,
              certified_supplier,
              is_leading_supplier
            ),
            mobile_number,
            phone
          )
        `)
        .eq('subcategory_id', subcategoryId);
        
      if (error) throw error;
      
      // Format the products to include image URLs and supplier information
      const formattedProducts = (data || []).map(product => {
        // Extract primary image or first image
        const productImages = product.product_images || [];
        const primaryImage = productImages.find((img: any) => img.is_primary);
        const firstImage = productImages.length > 0 ? productImages[0] : null;
        
        // Extract all image URLs sorted by display_order
        const images = productImages
          .sort((a: any, b: any) => (a.display_order || 0) - (b.display_order || 0))
          .map((img: any) => img.image_url);
        
        // Extract supplier information through accounts
        const supplierProfile = product.accounts?.supplier_profiles;
        const accountInfo = product.accounts;
        
        // Remove the nested objects and add formatted data
        const { product_images, accounts, ...productData } = product;
        
        return {
          ...productData,
          images,
          main_image: (primaryImage || firstImage)?.image_url || null,
          // Add supplier information from the database
          supplier_business_name: supplierProfile?.business_name,
          supplier_slug: supplierProfile?.slug,
          supplier_logo_url: supplierProfile?.logo_url,
          supplier_rating: supplierProfile?.rating || 0,
          supplier_review_count: supplierProfile?.review_count || 0,
          supplier_response_rate: supplierProfile?.response_rate || 0,
          supplier_mobile_number: supplierProfile?.mobile_number || accountInfo?.mobile_number || accountInfo?.phone,
          supplier_verified: supplierProfile?.verified_supplier || false,
          supplier_certified: supplierProfile?.certified_supplier || false,
          is_leading_supplier: supplierProfile?.is_leading_supplier || false,
        };
      });
      
      return formattedProducts;
    } catch (error) {
      this.handleError(error, 'fetching products by subcategory');
      return [];
    }
  }
  
  /**
   * Get products by product category ID
   * @param productCategoryId The product category ID
   * @returns Array of products or empty array if none found
   */
  async getProductsByProductCategoryId(productCategoryId: string): Promise<Product[]> {
    try {
      const { data, error } = await this.supabase
        .from('products')
        .select(`
          *,
          product_images (
            image_url,
            is_primary,
            display_order
          ),
          accounts!supplier_id (
            supplier_profiles (
              business_name,
              slug,
              logo_url,
              rating,
              review_count,
              response_rate,
              mobile_number,
              verified_supplier,
              certified_supplier,
              is_leading_supplier
            ),
            mobile_number,
            phone
          )
        `)
        .eq('product_category_id', productCategoryId);
        
      if (error) throw error;
      
      // Format the products to include image URLs and supplier information
      const formattedProducts = (data || []).map(product => {
        // Extract primary image or first image
        const productImages = product.product_images || [];
        const primaryImage = productImages.find((img: any) => img.is_primary);
        const firstImage = productImages.length > 0 ? productImages[0] : null;
        
        // Extract all image URLs sorted by display_order
        const images = productImages
          .sort((a: any, b: any) => (a.display_order || 0) - (b.display_order || 0))
          .map((img: any) => img.image_url);
        
        // Extract supplier information through accounts
        const supplierProfile = product.accounts?.supplier_profiles;
        const accountInfo = product.accounts;
        
        // Remove the nested objects and add formatted data
        const { product_images, accounts, ...productData } = product;
        
        return {
          ...productData,
          images,
          main_image: (primaryImage || firstImage)?.image_url || null,
          // Add supplier information from the database
          supplier_business_name: supplierProfile?.business_name,
          supplier_slug: supplierProfile?.slug,
          supplier_logo_url: supplierProfile?.logo_url,
          supplier_rating: supplierProfile?.rating || 0,
          supplier_review_count: supplierProfile?.review_count || 0,
          supplier_response_rate: supplierProfile?.response_rate || 0,
          supplier_mobile_number: supplierProfile?.mobile_number || accountInfo?.mobile_number || accountInfo?.phone,
          supplier_verified: supplierProfile?.verified_supplier || false,
          supplier_certified: supplierProfile?.certified_supplier || false,
          is_leading_supplier: supplierProfile?.is_leading_supplier || false,
        };
      });
      
      return formattedProducts;
    } catch (error) {
      this.handleError(error, 'fetching products by product category');
      return [];
    }
  }
  
  /**
   * Create a new product and increment the product count
   * @param product The product data
   * @returns The created product or null if creation failed
   */
  async createProduct(product: Omit<Product, 'id' | 'created_at' | 'updated_at'>): Promise<Product | null> {
    try {
      // Add default values and timestamps
      const productWithDefaults = {
        ...product,
        currency: product.currency || 'INR',
        listed_date: product.listed_date || new Date().toISOString(),
        views_count: 0,
        rating: 0,
        review_count: 0
      };
      
      const productWithTimestamps = this.addTimestamps(productWithDefaults, true);
      
      // Insert the product
      const { data: newProduct, error: productError } = await this.supabase
        .from('products')
        .insert(productWithTimestamps)
        .single();
        
      if (productError) throw productError;
      
      // Increment the product count in the product category
      await this.categoryService.updateProductCount(product.product_category_id, 1);
      
      return newProduct;
    } catch (error) {
      this.handleError(error, 'creating product');
      return null;
    }
  }
  
  /**
   * Update an existing product
   * @param id The product ID
   * @param updates The product updates
   * @returns The updated product or null if update failed
   */
  async updateProduct(id: string, updates: Partial<Product>): Promise<Product | null> {
    try {
      // Check if product category is being changed
      let oldProductCategoryId: string | null = null;
      
      if (updates.product_category_id) {
        const { data: currentProduct } = await this.supabase
          .from('products')
          .select('product_category_id')
          .eq('id', id)
          .single();
          
        if (currentProduct && currentProduct.product_category_id !== updates.product_category_id) {
          oldProductCategoryId = currentProduct.product_category_id;
        }
      }
      
      // Update the product with timestamp
      const updatesWithTimestamp = this.addTimestamps(updates);
      
      const { data: updatedProduct, error } = await this.supabase
        .from('products')
        .update(updatesWithTimestamp)
        .eq('id', id)
        .single();
        
      if (error) throw error;
      
      // If product category changed, update product counts
      if (oldProductCategoryId && updates.product_category_id) {
        // Decrement count in old category
        await this.categoryService.updateProductCount(oldProductCategoryId, -1);
        
        // Increment count in new category
        await this.categoryService.updateProductCount(updates.product_category_id, 1);
      }
      
      return updatedProduct;
    } catch (error) {
      this.handleError(error, 'updating product');
      return null;
    }
  }
  
  /**
   * Delete a product and decrement the product count
   * @param id The product ID
   * @returns True if deletion was successful, false otherwise
   */
  async deleteProduct(id: string): Promise<boolean> {
    try {
      // Get the product to get its category ID
      const { data: product, error: fetchError } = await this.supabase
        .from('products')
        .select('product_category_id')
        .eq('id', id)
        .single();
        
      if (fetchError) throw fetchError;
      
      // Delete the product
      const { error: deleteError } = await this.supabase
        .from('products')
        .delete()
        .eq('id', id);
        
      if (deleteError) throw deleteError;
      
      // Decrement the product count
      if (product && product.product_category_id) {
        await this.categoryService.updateProductCount(product.product_category_id, -1);
      }
      
      return true;
    } catch (error) {
      this.handleError(error, 'deleting product');
      return false;
    }
  }
  
  /**
   * Increment the view count for a product
   * @param id The product ID
   * @returns True if update was successful, false otherwise
   */
  async incrementViewCount(id: string): Promise<boolean> {
    try {
      const { data: product, error: fetchError } = await this.supabase
        .from('products')
        .select('views_count')
        .eq('id', id)
        .single();
        
      if (fetchError) throw fetchError;
      
      const newViewCount = (product.views_count || 0) + 1;
      
      const { error: updateError } = await this.supabase
        .from('products')
        .update({ 
          views_count: newViewCount,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);
        
      if (updateError) throw updateError;
      
      return true;
    } catch (error) {
      this.handleError(error, 'incrementing view count');
      return false;
    }
  }
  
  /**
   * Update product rating based on reviews
   * @param id The product ID
   * @returns True if update was successful, false otherwise
   */
  async updateProductRating(id: string): Promise<boolean> {
    try {
      // Get all reviews for the product
      const { data: reviews, error: reviewsError } = await this.supabase
        .from('product_reviews')
        .select('rating')
        .eq('product_id', id);
        
      if (reviewsError) throw reviewsError;
      
      // Calculate average rating
      const reviewCount = reviews?.length || 0;
      let averageRating = 0;
      
      if (reviewCount > 0) {
        const sum = reviews.reduce((total, review) => total + review.rating, 0);
        averageRating = parseFloat((sum / reviewCount).toFixed(2));
      }
      
      // Update the product
      const { error: updateError } = await this.supabase
        .from('products')
        .update({ 
          rating: averageRating,
          review_count: reviewCount,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);
        
      if (updateError) throw updateError;
      
      return true;
    } catch (error) {
      this.handleError(error, 'updating product rating');
      return false;
    }
  }
  
  /**
   * Get featured products
   * @param limit The maximum number of products to return (default: 8)
   * @returns Array of featured products or empty array if none found
   */
  async getFeaturedProducts(limit: number = 8): Promise<Product[]> {
    try {
      const { data, error } = await this.supabase
        .from('products')
        .select('*, accounts(company_name, full_name)')
        .eq('featured', true)
        .eq('in_stock', true)
        .order('created_at', { ascending: false })
        .limit(limit);
        
      if (error) throw error;
      
      // Format the product data to include supplier information
      const formattedProducts = data?.map(product => {
        const supplierAccount = product.accounts;
        delete product.accounts; // Remove the nested accounts object
        
        return {
          ...product,
          supplier_name: supplierAccount?.company_name || supplierAccount?.full_name || 'Unknown Supplier'
        };
      }) || [];
      
      return formattedProducts;
    } catch (error) {
      this.handleError(error, 'fetching featured products');
      return [];
    }
  }
  
  /**
   * Get all products (with optional limit for performance)
   * @param limit Optional limit on number of products to return
   * @returns Array of products or empty array if none found
   */
  async getAllProducts(limit: number = 1000): Promise<Product[]> {
    try {
      const { data, error } = await this.supabase
        .from('products')
        .select(`
          *,
          product_images (
            image_url,
            is_primary,
            display_order
          ),
          accounts!supplier_id (
            supplier_profiles (
              business_name,
              slug,
              logo_url,
              rating,
              review_count,
              response_rate,
              mobile_number,
              verified_supplier,
              certified_supplier,
              is_leading_supplier
            ),
            mobile_number,
            phone
          )
        `)
        .limit(limit);
        
      if (error) throw error;
      
      // Format the products to include image URLs and supplier information
      const formattedProducts = (data || []).map(product => {
        // Extract primary image or first image
        const productImages = product.product_images || [];
        const primaryImage = productImages.find((img: any) => img.is_primary);
        const firstImage = productImages.length > 0 ? productImages[0] : null;
        
        // Extract all image URLs sorted by display_order
        const images = productImages
          .sort((a: any, b: any) => (a.display_order || 0) - (b.display_order || 0))
          .map((img: any) => img.image_url);
        
        // Extract supplier information through accounts
        const supplierProfile = product.accounts?.supplier_profiles;
        const accountInfo = product.accounts;
        
        // Remove the nested objects and add formatted data
        const { product_images, accounts, ...productData } = product;
        
        return {
          ...productData,
          images,
          main_image: (primaryImage || firstImage)?.image_url || null,
          // Add supplier information from the database
          supplier_business_name: supplierProfile?.business_name,
          supplier_slug: supplierProfile?.slug,
          supplier_logo_url: supplierProfile?.logo_url,
          supplier_rating: supplierProfile?.rating || 0,
          supplier_review_count: supplierProfile?.review_count || 0,
          supplier_response_rate: supplierProfile?.response_rate || 0,
          supplier_mobile_number: supplierProfile?.mobile_number || accountInfo?.mobile_number || accountInfo?.phone,
          supplier_verified: supplierProfile?.verified_supplier || false,
          supplier_certified: supplierProfile?.certified_supplier || false,
          is_leading_supplier: supplierProfile?.is_leading_supplier || false,
        };
      });
      
      return formattedProducts;
    } catch (error) {
      this.handleError(error, 'fetching all products');
      return [];
    }
  }
}
